import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    Divider,
    Grid,
    MenuItem,
    Select,
    Skeleton,
} from "@mzc-pdc/ui";
import Typography from "@components_v2/typography/typography";
import theme from "@styles/theme";
import { IconMetadata } from "@mzc-cloudplex/icons";
import { StyledGrid } from "@components_v2/grid/grid.styled";
import MetadataSkeletonCreateAsset from "@routes/metadata-skeleton-create-asset";
import { updateData } from "@modules/createAsset";
import {
    clearMetadataTemplate,
    getDetailMetadataTemplate,
    getMetadataTemplate,
} from "@modules/metadataTemplate";
import { getCategoryGroupAPI } from "@modules/categoryGroupsManagement";
import { getCategoryAPI } from "@modules/categoriesManagement";

import CategoriesField from "./metadata-fields/categories-field";
import AttributionsField from "./metadata-fields/attributions-field";
import TagsField from "./metadata-fields/tags-field";
import DescriptionField from "./metadata-fields/description-field";

const CreateAssetMetadataSection = ({ pendingCreate, initialCategory }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);

    const { metadataTemplate, detailMetadataTemplate, defaultMetadataId } = useSelector(
        ({ metadataTemplate }) => ({
            metadataTemplate: metadataTemplate.metadataTemplateList,
            detailMetadataTemplate: metadataTemplate.detailMetadataTemplate,
            defaultMetadataId: metadataTemplate.defaultMetadataId,
        }),
    );

    const [categoryGroup, setCategoryGroup] = useState(null);
    const [categoryGroupTooltipDisabled, setCategoryGroupTooltipDisabled] = useState(false);
    const [pendingMetadata, setPendingMetadata] = useState(true);

    const loadDetailMetadata = async (metadataTemplateId) => {
        if (!metadataTemplateId) return;

        const detail = await dispatch(
            getDetailMetadataTemplate({ templateId: metadataTemplateId }),
        );
        setCategoryGroup(detail.template.categoryGroup);
        dispatch(
            updateData({
                name: detail.template.namePrefix,
                categories: detail.template.categories,
                attributions: detail.template.attributions?.map((attr, index) => ({
                    ...attr,
                    id: index,
                })),
                tags: detail.template.tags?.map((tag) => tag.value),
            }),
        );
    };

    const loadDefaultMetadata = async () => {
        try {
            setPendingMetadata(true);
            const response = await dispatch(getMetadataTemplate({ domain: "ASSET" }));
            const { defaultMetadataId: defaultId } = response;
            if (defaultId) await loadDetailMetadata(defaultId);
        } catch (e) {
            console.log(e);
        } finally {
            setPendingMetadata(false);
        }
    };

    const handleInitialCategory = async (category) => {
        setPendingMetadata(true);
        try {
            await dispatch(getMetadataTemplate({ domain: "ASSET", excludeDefaultMetadata: true }));

            if (category?.categoryGroupName && category?.categoryName) {
                setCategoryGroup({
                    id: category.categoryGroupId,
                    name: category.categoryGroupName,
                });
                dispatch(
                    updateData({
                        categories: [
                            {
                                id: category.categoryId,
                                name: category.categoryName,
                            },
                        ],
                    }),
                );
            } else {
                try {
                    const { data: categoryGroupData } = await getCategoryGroupAPI(
                        stage.id,
                        stage.endpoint,
                        project.id,
                        category.categoryGroupId,
                    );
                    const { data: categoryData } = await getCategoryAPI(
                        stage.id,
                        stage.endpoint,
                        project.id,
                        category.categoryGroupId,
                        category.categoryId,
                    );

                    setCategoryGroup({ id: categoryGroupData.id, name: categoryGroupData.name });
                    dispatch(updateData({ categories: [categoryData] }));
                } catch (error) {
                    console.error("Failed to load category:", error);
                }
            }
        } finally {
            setPendingMetadata(false);
        }
    };

    useEffect(() => {
        if (initialCategory) {
            handleInitialCategory(initialCategory);
        } else {
            loadDefaultMetadata();
        }
        return () => {
            dispatch(clearMetadataTemplate());
        };
    }, []);

    return (
        <Grid container alignItems="start" flexDirection={`column`} gap={1}>
            <Grid container flexDirection={"row"} alignItems={"center"}>
                <Grid item flex={1}>
                    <Typography
                        variant={`body1`}
                        component={"p"}
                        color={theme.palette.secondary[800]}
                    >
                        {t(`common::label::Metadata`)}
                    </Typography>
                </Grid>
                <Select
                    variant={"filled"}
                    displayEmpty
                    onChange={(e) => loadDetailMetadata(e.target.value)}
                    sx={{ backgroundColor: theme.palette.common.white }}
                    value={detailMetadataTemplate.data?.id || defaultMetadataId}
                    renderValue={(selected) => {
                        if (!selected) {
                            return (
                                <Grid container flexDirection={"row"} gap={1} alignItems="center">
                                    <IconMetadata size={16} />
                                    <Typography
                                        variant={"body3"}
                                        color={theme.palette.secondary[800]}
                                    >
                                        {t(`common::label::Load Metadata`)}
                                    </Typography>
                                </Grid>
                            );
                        }

                        const selectedTemplate = metadataTemplate?.data?.find(
                            (template) => template.id === selected,
                        );

                        return (
                            <Grid container flexDirection={"row"} gap={1} alignItems="center">
                                <IconMetadata size={16} color={theme.palette.secondary[700]} />
                                <Typography variant="body1">{selectedTemplate?.name}</Typography>
                            </Grid>
                        );
                    }}
                >
                    {metadataTemplate?.pending ? (
                        <MenuItem>
                            <Grid container flexDirection={"column"} px={1.5} py={0.5} gap={1}>
                                {new Array(3).fill("").map((_, i) => (
                                    <Grid item key={`metadata-template-skeleton-${i}`}>
                                        <Skeleton
                                            variant={"rounded"}
                                            animation={"wave"}
                                            width={150}
                                            height={22}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </MenuItem>
                    ) : (
                        metadataTemplate?.data?.map((option, index) => (
                            <MenuItem key={`metadataTemplate-${index}`} value={option.id}>
                                <Typography component="span" variant="body1">
                                    {option.name}
                                </Typography>
                            </MenuItem>
                        ))
                    )}
                </Select>
            </Grid>
            <StyledGrid item theme={theme}>
                {pendingMetadata ? (
                    <MetadataSkeletonCreateAsset />
                ) : (
                    <>
                        <CategoriesField
                            categoryGroup={categoryGroup}
                            setCategoryGroup={setCategoryGroup}
                            categoryGroupTooltipDisabled={categoryGroupTooltipDisabled}
                            setCategoryGroupTooltipDisabled={setCategoryGroupTooltipDisabled}
                        />
                        <AttributionsField />
                        <TagsField pendingCreate={pendingCreate} />
                        <Divider sx={{ mt: 1.5, mb: 2.5 }} />
                        <DescriptionField />
                    </>
                )}
            </StyledGrid>
        </Grid>
    );
};

export default CreateAssetMetadataSection;
